export const MANILA_TIME_ZONE = 'Asia/Manila';

const hasExplicitTimezone = (value) => /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);

/**
 * Parse a timestamp into a correct UTC instant.
 * Timezone-naive datetime strings from this app represent UTC and are normalized with `Z`.
 * Strings that already include `Z` or an offset are preserved.
 */
export const parseInstant = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  let normalized = raw;
  if (!hasExplicitTimezone(normalized)) {
    normalized = normalized.includes('T') ? normalized : normalized.replace(' ', 'T');
    normalized = `${normalized}Z`;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const toDateOnlyYmd = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const dateOnly = text.slice(0, 10);
  return YMD_PATTERN.test(dateOnly) ? dateOnly : '';
};

export const getManilaCalendarYmd = (now = Date.now()) => {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: MANILA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

const addCalendarDays = (ymd, days) => {
  const match = YMD_PATTERN.exec(toDateOnlyYmd(ymd));
  if (!match) return '';
  const utcDate = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + Number(days || 0)));
  const year = utcDate.getUTCFullYear();
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getManilaTomorrowYmd = (now = Date.now()) => addCalendarDays(getManilaCalendarYmd(now), 1);

export const isManilaFutureDate = (ymd, now = Date.now()) => {
  const date = toDateOnlyYmd(ymd);
  const today = getManilaCalendarYmd(now);
  return Boolean(date && today && date > today);
};

/** True when the calendar date is strictly before Manila today (today is not past). */
export const isManilaPastDate = (ymd, now = Date.now()) => {
  const date = toDateOnlyYmd(ymd);
  const today = getManilaCalendarYmd(now);
  return Boolean(date && today && date < today);
};

export const isAllowedFutureOrUnchangedDate = (ymd, { originalYmd = '', now = Date.now() } = {}) => {
  const selected = toDateOnlyYmd(ymd);
  if (!selected) return true;
  if (isManilaFutureDate(selected, now)) return true;
  const original = toDateOnlyYmd(originalYmd);
  return Boolean(original && selected === original);
};

export const getDateInputMinYmd = ({ originalYmd = '', now = Date.now() } = {}) => {
  const original = toDateOnlyYmd(originalYmd);
  if (original && !isManilaFutureDate(original, now)) return '';
  return getManilaTomorrowYmd(now);
};

export const FUTURE_DATE_ERROR = 'Please choose a future date.';

/**
 * Relative time labels for notification timestamps.
 * @param {string|number|Date|null|undefined} value
 * @param {number} [now=Date.now()] Fixed clock for deterministic tests.
 */
export const formatTimeAgo = (value, now = Date.now()) => {
  const date = parseInstant(value);
  if (!date) return 'Just now';

  const diffSeconds = Math.floor((now - date.getTime()) / 1000);
  if (diffSeconds < 60) return 'Just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const formatManilaDateTime = (value) => {
  const parsed = parseInstant(value);
  if (!parsed) return '-';

  return parsed.toLocaleString('en-PH', {
    timeZone: MANILA_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
